import React from 'react';
import styled, { useTheme } from 'styled-components';

type TProps = {
    type?: 'bounce' | 'ripple' | 'spin' | 'logo';
    dotSize?: number;
    color?: string | 'light' | 'brand' | 'dark';
    fullScreen?: boolean;
    size?: number;
};

const Loader = ({
    color,
    fullScreen,
    dotSize = fullScreen ? 20 : 6,
    type = 'bounce',
    size = 24,
}: TProps): JSX.Element => {
    const theme = useTheme(); // #TODO: @Nipun - this should be implied

    const getLoader = () => {
        switch (type) {
            case 'bounce':
                return (
                    <BounceWrapper>
                        <BounceChild
                            height={dotSize}
                            width={dotSize}
                            color={color || theme.colors.bodyAlt}
                            delay="-0.32s"
                        />
                        <BounceChild
                            height={dotSize}
                            width={dotSize}
                            color={color || theme.colors.bodyAlt}
                            delay="-0.16s"
                        />
                        <BounceChild height={dotSize} width={dotSize} color={color || theme.colors.bodyAlt} />
                    </BounceWrapper>
                );

            case 'ripple':
                return (
                    <RippleWrapper>
                        <Ripple>
                            <RippleChild color={color || theme.colors.bodyAlt} />
                            <RippleChild color={color || theme.colors.bodyAlt} />
                        </Ripple>
                    </RippleWrapper>
                );

            case 'spin':
                return (
                    <SpinLoaderWrapper>
                        <SpinLoader />
                    </SpinLoaderWrapper>
                );
            case 'logo':
                return (
                    <LogoLoaderWrapper>
                        <LogoLoader style={{ width: size, height: size }}>
                            <svg
                                width="50"
                                height="50"
                                viewBox="0 0 50 50"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M0 26.185C0 39.5863 11.4513 50 24.7678 50C38.7054 50 50 38.8138 50 25.1542C50 17.1639 46.115 10.1543 40.3625 5.46341L36.1665 12.6789C39.6386 15.6188 42.0208 20.1538 42.0208 25.2566C42.0208 34.5359 34.3001 42.1644 24.7678 42.1644C15.2848 42.1644 7.56406 34.5359 7.56406 24.9994C7.56406 23.8651 7.667 22.7319 7.87513 21.6488L0 26.185ZM32.5389 1.23673C30.1567 0.360665 28.0307 0 25.9069 0C24.3001 0 22.9014 0.205935 21.8653 0.410758L16.4764 9.6901C18.4458 8.55578 20.9847 7.93798 23.7317 7.93798C25.3374 7.93798 26.7886 8.14391 28.2903 8.55578L32.5389 1.23673Z"
                                    fill="currentColor"
                                />
                            </svg>
                        </LogoLoader>
                    </LogoLoaderWrapper>
                );
            default:
                return <div />;
        }
    };

    return fullScreen ? <FullScreenLoader>{getLoader()}</FullScreenLoader> : getLoader();
};

const FullScreenLoader = styled.div`
    height: 100vh; /* cli - minus the header */
    display: flex;
    justify-content: center;
    align-items: center;
`;

/* INDEX
1. Bounce Loader Styles
2. Ripple Loader Styles
3. Spin Loader styles
*/

/* 1. Bounce Loader Styles */
const BounceWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
`;

type TBounceChildProps = { color: string; height: number; width: number; delay?: string };
const BounceChild = styled.div<TBounceChildProps>`
    border-radius: 100%;
    margin: 0 2px;
    ${({ width }): string => `width: ${width}px`};
    ${({ height }): string => `height: ${height}px`};

    ${({ color, theme }): string => {
        let bgColor = color;
        if (color === 'brand') {
            bgColor = theme.colors.brandPrimary;
        }
        if (color === 'dark') {
            bgColor = theme.colors.body;
        }
        return `background-color: ${bgColor}`;
    }};

    animation-name: bounce;
    animation-duration: 1.4s;
    animation-fill-mode: ease-in-out;
    animation-iteration-count: infinite;
    animation-direction: both;
    ${({ delay }): string => (delay ? `animation-delay: ${delay}` : '')};

    @keyframes bounce {
        0% {
            -webkit-transform: scale(0);
            transform: scale(0);
        }
        80% {
            -webkit-transform: scale(0);
            transform: scale(0);
        }
        100% {
            -webkit-transform: scale(0);
            transform: scale(0);
        }
        40% {
            -webkit-transform: scale(1);
            transform: scale(1);
        }
    }
`;

/* 2. Ripple Loader Styles */
const RippleWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
`;

const Ripple = styled.div`
    width: 44px;
    height: 44px;
    position: relative;
`;

type TRippleChildProps = { color: string };
const RippleChild = styled.div<TRippleChildProps>`
    box-sizing: content-box;
    position: absolute;
    border: 2px solid;
    border-radius: 50%;
    -webkit-animation: loader-ripple 1s cubic-bezier(0, 0.2, 0.8, 1) infinite;
    animation: loader-ripple 1s cubic-bezier(0, 0.2, 0.8, 1) infinite;

    ${({ color, theme }): string => {
        let borderColor = color;
        if (color === 'brand') {
            borderColor = theme.colors.brandPrimary;
        }
        if (color === 'dark') {
            borderColor = theme.colors.body;
        }
        return `border-color: ${borderColor}`;
    }};

    &:nth-child(2) {
        -webkit-animation-delay: -0.5s;
        animation-delay: -0.5s;
    }

    @keyframes loader-ripple {
        0% {
            top: 20px;
            left: 20px;
            width: 0;
            height: 0;
            opacity: 1;
        }
        100% {
            top: 0;
            left: 0;
            width: 40px;
            height: 40px;
            opacity: 0;
        }
    }
`;

/* 3. Spin Loader Styles */
const SpinLoaderWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
`;

const SpinLoader = styled.div`
    border-radius: 50%;
    border: 4px solid ${({ theme }): string => theme.colors.brandPrimaryLight};
    border-top-color: ${({ theme }): string => theme.colors.brandPrimary};
    width: 24px;
    height: 24px;
    animation: spin 2s linear infinite;

    @keyframes spin {
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }
    }
`;

/* 4. Logo Loader Styles */
const LogoLoaderWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
`;

const LogoLoader = styled.div`
    color: ${({ theme }): string => theme.colors.bodyAlt};

    svg {
        display: block;
        width: 100%;
        height: 100%;
        animation: spin 2s linear infinite;
    }

    @keyframes spin {
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }
    }
`;

export default Loader;
